import { chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
// Utils
import type { List } from 'immutable';
import { TransactionItem } from '../transactions/transactionsTypes';
import type { ExpansionPanelItem, GSFStage, PlannedSpendingCardData, PlannedSpendingItem } from './freeToSpendTypes';
import { ExpansionPanelItemTypeEnum, GSFStageEnum, GSF_STAGE_ORDER } from './freeToSpendTypes';

// activeStage: The current phase of the GSF
// query: The phase you want to check is complete or not
// skipGettingStartedFlow: If the user has gone through an older version of the getting started flow, they should ALWAYS be completed with the new GSF
export const isStageComplete = (activeStage: GSFStage, query: GSFStage, skipGettingStartedFlow: boolean): boolean => {
  return (
    GSF_STAGE_ORDER.indexOf(activeStage) > GSF_STAGE_ORDER.indexOf(query) ||
    activeStage === GSFStageEnum.COMPLETE ||
    skipGettingStartedFlow
  );
};

export const getPlannedSpendingCardData = (
  plannedItem: PlannedSpendingItem,
  isPreviousMonth: boolean,
): PlannedSpendingCardData => {
  const target = (plannedItem.overwrittenTargetAmount ?? plannedItem.targetAmount) || 0;
  const calculatedSpentAmount = plannedItem.calculatedSpentAmount || 0;
  const leftToSpend = target + calculatedSpentAmount;
  const overSpent = leftToSpend < 0;
  const showUnused = isPreviousMonth && Math.abs(calculatedSpentAmount) < Math.abs(target);
  return { ...plannedItem, target, calculatedSpentAmount, leftToSpend, overSpent, showUnused };
};

export const getWidthPercent = (spent: number, plannedSpend: number, rollover: number) => {
  let widthPercent = 0;
  let widthPercentText = 0;
  let rolloverPercent = 0;
  let leftPercent = 0;
  let originalRolloverPercent = 0;

  const safeSpent = !Number.isNaN(spent) ? spent : 0;
  const safePlannedSpend = !Number.isNaN(plannedSpend) ? plannedSpend : 0;
  const safeRollover = !Number.isNaN(rollover) ? rollover : 0;

  if (safeRollover > 0) {
    // Calculate spent %
    if (safeSpent > 0 && Math.abs(safeSpent) < Math.abs(safePlannedSpend) + safeRollover) {
      widthPercent = (Math.abs(safeSpent) / (Math.abs(safePlannedSpend) + safeRollover)) * 100;
    }
    if (Math.abs(safeSpent) >= Math.abs(plannedSpend) + safeRollover) {
      widthPercent = 100;
    }

    widthPercentText = (Math.abs(safeSpent) / (Math.abs(safePlannedSpend) + safeRollover)) * 100;

    if (safeSpent <= 0) {
      widthPercentText = 0;
      widthPercent = 0;
    }

    // Calculate rollover %
    rolloverPercent = (Math.abs(safeRollover) / (safePlannedSpend + Math.abs(safeRollover))) * 100;
    originalRolloverPercent = rolloverPercent;
    if (100 - rolloverPercent - widthPercent <= 0) {
      rolloverPercent = 100 - widthPercent;
    }

    // Calculate Left %
    leftPercent = 100 - rolloverPercent - widthPercent;
  } else if (safeRollover < 0) {
    // Calculate rollover %
    rolloverPercent = (Math.abs(safeRollover) / safePlannedSpend) * 100;
    originalRolloverPercent = rolloverPercent;

    // Calculate spent %
    if (safeSpent > 0 && Math.abs(safeSpent) + Math.abs(safeRollover) < Math.abs(safePlannedSpend)) {
      widthPercent = (Math.abs(safeSpent) / Math.abs(safePlannedSpend)) * 100;
    }
    if (Math.abs(safeSpent) + Math.abs(safeRollover) >= Math.abs(plannedSpend)) {
      widthPercent = 100 - rolloverPercent; // Can become negative
    }

    widthPercentText = ((Math.abs(safeSpent) + Math.abs(safeRollover)) / Math.abs(safePlannedSpend)) * 100;

    if (safeSpent <= 0) {
      // We want to show the text for what it is if rollovers fill up the entire bar!
      if (rolloverPercent < 100) {
        widthPercentText = 0;
      }
      widthPercent = 0;
    }
    if (widthPercent < 0) {
      widthPercent = 0;
    }

    // Calculate Left %
    leftPercent = 100 - rolloverPercent - widthPercent;
  } else {
    if (safeSpent > 0 && Math.abs(safeSpent) < Math.abs(safePlannedSpend)) {
      widthPercent = (Math.abs(safeSpent) / Math.abs(safePlannedSpend)) * 100;
    }
    if (Math.abs(safeSpent) >= Math.abs(plannedSpend)) {
      widthPercent = 100;
    }
    if (safeSpent < 0) {
      widthPercent = 0;
    }
    widthPercentText = (Math.abs(safeSpent) / Math.abs(safePlannedSpend)) * 100;
    leftPercent = 100 - widthPercent;
    rolloverPercent = 0;
  }

  return {
    originalRolloverPercent,
    leftPercent,
    widthPercent,
    // We want to show the text if the negative rollover fills up the bar completely!
    widthPercentText: widthPercent > 7.9 || (rolloverPercent >= 100 && safeRollover < 0) ? widthPercentText : null,
    rolloverPercent,
  };
};

export const getAllMatchedTransactionArrays = (transferTransactionsItems: List<TransactionItem> | undefined) => {
  const retObj: Record<string, TransactionItem[]> = {};
  const matchedArrayIds: string[] = [];
  transferTransactionsItems?.forEach((txnItem) => {
    if (Object.values(retObj).length + matchedArrayIds.length !== transferTransactionsItems?.size) {
      // check if we have paired all txns and no longer need to search
      const transactionItem = txnItem.item;
      const transactionItemId = transactionItem.id;
      if (transactionItemId && !matchedArrayIds.includes(transactionItemId)) {
        if (!(transactionItemId in retObj)) {
          retObj[transactionItemId] = [];
        }
        retObj[transactionItemId].push(txnItem);
        const otherSideOfTransfer = transferTransactionsItems?.find(
          (transferTxn) =>
            (transferTxn.item.transfer?.id === transactionItemId ||
              transferTxn.item.id === transactionItem.transfer?.id) &&
            transactionItem.coa?.id === transferTxn.item.accountId &&
            transferTxn.item.amount === transactionItem.amount * -1 &&
            transactionItem.postedOn === transferTxn.item.postedOn,
        );
        if (otherSideOfTransfer && otherSideOfTransfer.item.id && !(otherSideOfTransfer.item.id in retObj)) {
          if (otherSideOfTransfer.item.amount > 0) {
            retObj[transactionItemId].push(otherSideOfTransfer);
          } else {
            retObj[transactionItemId].unshift(otherSideOfTransfer);
          }
          matchedArrayIds.push(otherSideOfTransfer.item.id);
        }
      }
    }
  });
  return Object.values(retObj);
};

const isTransfer = (txn: ExpansionPanelItem) => {
  if (txn?.type === ExpansionPanelItemTypeEnum.TRANSFER) {
    return true;
  } else if (txn?.type === ExpansionPanelItemTypeEnum.TXN) {
    return chartOfAccountsUtils.isTransferCOA(txn?.txnItem?.item?.coa);
  }
  return false;
};

const normalizeTxns = (txns: TransactionItem[] | TransactionItem[][] | undefined) => {
  if (!txns) return;

  return Object.values(txns).map((txnArr) => {
    let sumAmount = 0;
    txnArr?.forEach((txnItem: TransactionItem) => {
      if (!txnItem.item.isExcludedFromF2S) {
        sumAmount = sumAmount + txnItem.item.amount;
      }
    });

    if (txnArr?.length > 1) {
      return { type: ExpansionPanelItemTypeEnum.TRANSFER, txnItemArray: txnArr, amount: sumAmount };
    } else {
      return { type: ExpansionPanelItemTypeEnum.TXN, txnItem: txnArr[0], amount: sumAmount };
    }
  });
};

export const getNormalizedFilteredTxns = (txns: List<TransactionItem> | undefined, filter: string | undefined) => {
  const allTxns: TransactionItem[] | TransactionItem[][] | undefined = getAllMatchedTransactionArrays(txns);
  if (!allTxns) return;

  let filteredTxns: ExpansionPanelItem[] | undefined = normalizeTxns(allTxns);
  if (!filteredTxns) return;

  if (filter === 'bills') {
    filteredTxns = filteredTxns.filter(
      (txn) => txn.type === ExpansionPanelItemTypeEnum.TXN && !txn.txnItem.item.isSubscription && !isTransfer(txn),
    );
  } else if (filter === 'subscriptions') {
    filteredTxns = filteredTxns.filter(
      (txn) => txn.type === ExpansionPanelItemTypeEnum.TXN && txn.txnItem.item.isSubscription && !isTransfer(txn),
    );
  } else if (filter === 'transfers') {
    filteredTxns = filteredTxns.filter((txn) => txn.type === ExpansionPanelItemTypeEnum.TRANSFER || isTransfer(txn));
  }

  return filteredTxns;
};
